import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiSave, FiPlus, FiTrash2, FiMove, FiPlay, FiVideo, FiImage } from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { VideoSectionContent, VideoItem } from '../../../types/content'
import VideoPicker from '../VideoPicker'
import MediaPicker from '../MediaPicker'
import toast from 'react-hot-toast'

const VideoSectionEditor: React.FC = () => {
  const { content, loading, saving, saveContent } = useContent('videos')
  const [formData, setFormData] = useState<VideoSectionContent>({
    title: '',
    subtitle: '',
    description: '',
    videos: []
  })
  const [showVideoPicker, setShowVideoPicker] = useState(false)
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [currentVideoIndex, setCurrentVideoIndex] = useState<number | null>(null)
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({})
  const [highlightedVideo, setHighlightedVideo] = useState<number | null>(null)

  useEffect(() => {
    console.log('📡 Content changed:', content)
    if (content?.data) {
      const videoData = content.data as VideoSectionContent
      console.log('📥 Loading video data into form:', JSON.stringify(videoData, null, 2))
      setFormData(videoData)
    }
  }, [content])
  
  // Debug: Log formData changes
  useEffect(() => {
    console.log('📝 FormData updated:', JSON.stringify(formData, null, 2))
  }, [formData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    console.log('🎬 VideoSectionEditor: handleSubmit called')
    console.log('📦 Current formData:', JSON.stringify(formData, null, 2))
    
    // Clear previous validation errors
    setValidationErrors({})
    
    // Validate required fields
    const errors: {[key: string]: boolean} = {}
    let hasErrors = false
    
    formData.videos.forEach((video, index) => {
      if (!video.title.trim()) {
        console.log(`❌ Validation error: Video ${index} - missing title`)
        errors[`title-${index}`] = true
        hasErrors = true
      }
      if (!video.artist.trim()) {
        console.log(`❌ Validation error: Video ${index} - missing artist`)
        errors[`artist-${index}`] = true
        hasErrors = true
      }
      if (!video.videoUrl.trim()) {
        console.log(`❌ Validation error: Video ${index} - missing videoUrl`)
        errors[`videoUrl-${index}`] = true
        hasErrors = true
      }
      if (!video.runtime.trim()) {
        console.log(`❌ Validation error: Video ${index} - missing runtime`)
        errors[`runtime-${index}`] = true
        hasErrors = true
      }
    })
    
    if (hasErrors) {
      console.log('❌ Validation failed, errors:', errors)
      setValidationErrors(errors)
      toast.error('Please fill in all required fields (Title, Artist, Video URL, and Runtime)')
      return
    }
    
    console.log('✅ Validation passed, attempting to save...')
    console.log('💾 Saving data:', JSON.stringify(formData, null, 2))
    
    try {
      const success = await saveContent(formData)
      console.log('💾 Save result:', success)
      
      if (success) {
        console.log('✅ Video section saved successfully!')
        toast.success('Video section updated successfully!')
      } else {
        console.log('❌ Save returned false')
        toast.error('Failed to save video section')
      }
    } catch (error) {
      console.error('❌ Error during save:', error)
      toast.error('An error occurred while saving')
    }
  }

  const addVideo = () => {
    const newVideo: VideoItem = {
      id: `video-${Date.now()}`,
      title: 'New Video',
      artist: 'Artist Name',
      poster: '',
      videoUrl: '',
      runtime: '0:00',
      description: '',
      order: formData.videos.length + 1
    }
    setFormData({
      ...formData,
      videos: [...formData.videos, newVideo]
    })
    
    // Highlight the newly added video
    const newVideoIndex = formData.videos.length
    setHighlightedVideo(newVideoIndex)
    
    // Auto-scroll to the newly added video after a short delay to allow for DOM update
    setTimeout(() => {
      const videoElement = document.querySelector(`[data-video-index="${newVideoIndex}"]`)
      if (videoElement) {
        // Get the element's position and scroll with a small offset
        const elementRect = videoElement.getBoundingClientRect()
        const absoluteElementTop = elementRect.top + window.pageYOffset
        const offset = 20 // Small offset from the top
        
        window.scrollTo({
          top: absoluteElementTop - offset,
          behavior: 'smooth'
        })
        
        // Focus on the title input for immediate editing
        const titleInput = videoElement.querySelector('input[placeholder="Video Title"]') as HTMLInputElement
        if (titleInput) {
          titleInput.focus()
          titleInput.select() // Select the placeholder text so user can immediately type
        }
      }
    }, 100)
    
    // Remove highlight after 3 seconds
    setTimeout(() => {
      setHighlightedVideo(null)
    }, 3000)
  }

  const updateVideo = (index: number, field: keyof VideoItem, value: string | number) => {
    const updatedVideos = formData.videos.map((video, i) => 
      i === index ? { ...video, [field]: value } : video
    )
    setFormData({
      ...formData,
      videos: updatedVideos
    })
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[`${field}-${index}`]) {
      setValidationErrors(prev => ({
        ...prev,
        [`${field}-${index}`]: false
      }))
    }
    
    // Clear highlight when user starts editing
    if (highlightedVideo === index) {
      setHighlightedVideo(null)
    }
  }

  const removeVideo = (index: number) => {
    const updatedVideos = formData.videos.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      videos: updatedVideos
    })
  }

  const moveVideo = (index: number, direction: 'up' | 'down') => {
    const newVideos = [...formData.videos]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    
    if (targetIndex >= 0 && targetIndex < newVideos.length) {
      [newVideos[index], newVideos[targetIndex]] = [newVideos[targetIndex], newVideos[index]]
      
      // Update order values
      newVideos.forEach((video, i) => {
        video.order = i + 1
      })
      
      setFormData({
        ...formData,
        videos: newVideos
      })
    }
  }

  const handleVideoPickerOpen = (index: number) => {
    setCurrentVideoIndex(index)
    setShowVideoPicker(true)
  }

  const handleMediaPickerOpen = (index: number) => {
    setCurrentVideoIndex(index)
    setShowMediaPicker(true)
  }

  const handleVideoSelect = (videoUrl: string) => {
    if (currentVideoIndex !== null) {
      updateVideo(currentVideoIndex, 'videoUrl', videoUrl)
    }
    setShowVideoPicker(false)
    setCurrentVideoIndex(null)
  }

  const handlePosterSelect = (posterUrl: string) => {
    if (currentVideoIndex !== null) {
      updateVideo(currentVideoIndex, 'poster', posterUrl)
    }
    setShowMediaPicker(false)
    setCurrentVideoIndex(null)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-200 rounded w-1/4"></div>
          <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-neutral-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-neutral-800">Video Section Settings</h2>
          <p className="text-neutral-600 text-sm mt-1">
            Configure the video section content and manage your video library.
          </p>
        </div>
      </div>

      <form 
        onSubmit={(e) => {
          console.log('📋 Form onSubmit event triggered!')
          handleSubmit(e)
        }} 
        className="space-y-8"
      >
        {/* Section Content */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-800">Section Content</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Live"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Subtitle
              </label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Performances"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Section description..."
            />
          </div>
        </div>

        {/* Videos */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-neutral-800">Videos</h3>
            <button
              type="button"
              onClick={addVideo}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <FiPlus size={16} />
              <span>Add Video</span>
            </button>
          </div>

          <div className="space-y-4">
            {formData.videos.map((video, index) => (
              <motion.div
                key={video.id}
                data-video-index={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-6 border rounded-lg transition-all duration-500 ${
                  highlightedVideo === index
                    ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                    : 'border-neutral-200'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-neutral-800">
                    Video {index + 1}: {video.title || 'Untitled'}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => moveVideo(index, 'up')}
                      disabled={index === 0}
                      className="p-2 text-neutral-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <FiMove size={16} className="rotate-180" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveVideo(index, 'down')}
                      disabled={index === formData.videos.length - 1}
                      className="p-2 text-neutral-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <FiMove size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeVideo(index)}
                      className="p-2 text-red-500 hover:text-red-700"
                      title="Remove video"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={video.title}
                      onChange={(e) => updateVideo(index, 'title', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        validationErrors[`title-${index}`] 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-neutral-300'
                      }`}
                      placeholder="Video Title"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Artist *
                    </label>
                    <input
                      type="text"
                      value={video.artist}
                      onChange={(e) => updateVideo(index, 'artist', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        validationErrors[`artist-${index}`] 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-neutral-300'
                      }`}
                      placeholder="Artist Name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Runtime *
                    </label>
                    <input
                      type="text"
                      value={video.runtime}
                      onChange={(e) => updateVideo(index, 'runtime', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        validationErrors[`runtime-${index}`] 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-neutral-300'
                      }`}
                      placeholder="3:45"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Video URL *
                    </label>
                    
                    <div className="space-y-3">
                      {/* Video Preview */}
                      {video.videoUrl && (
                        <div className="w-full aspect-video rounded-lg overflow-hidden border border-neutral-200">
                          <video
                            src={video.videoUrl}
                            className="w-full h-full object-cover"
                            controls
                            muted
                            preload="metadata"
                          />
                        </div>
                      )}

                      {/* URL Input with Browse Button */}
                      <div className="flex space-x-3">
                        <div className="flex-1 relative">
                          <FiVideo className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
                          <input
                            type="text"
                            value={video.videoUrl}
                            onChange={(e) => updateVideo(index, 'videoUrl', e.target.value)}
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                              validationErrors[`videoUrl-${index}`] 
                                ? 'border-red-500 bg-red-50' 
                                : 'border-neutral-300'
                            }`}
                            placeholder="YouTube embed URL or uploaded video"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleVideoPickerOpen(index)}
                          className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors flex items-center space-x-2"
                        >
                          <FiVideo size={16} />
                          <span>Browse</span>
                        </button>
                      </div>
                      
                      <p className="text-sm text-neutral-600">
                        Use YouTube embed URL or click "Browse" to select from uploaded videos
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Order
                    </label>
                    <input
                      type="number"
                      value={video.order}
                      onChange={(e) => updateVideo(index, 'order', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="1"
                    />
                  </div>
                  
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Poster Image URL
                    </label>
                    
                    <div className="space-y-3">
                      {/* Poster Preview */}
                      {video.poster && (
                        <div className="w-32 h-20 rounded-lg overflow-hidden border border-neutral-200">
                          <img
                            src={video.poster}
                            alt="Poster Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* URL Input with Browse Button */}
                      <div className="flex space-x-3">
                        <div className="flex-1 relative">
                          <FiImage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
                          <input
                            type="text"
                            value={video.poster || ''}
                            onChange={(e) => updateVideo(index, 'poster', e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Poster image URL (optional)"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleMediaPickerOpen(index)}
                          className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors flex items-center space-x-2"
                        >
                          <FiImage size={16} />
                          <span>Browse</span>
                        </button>
                      </div>
                      
                      <p className="text-sm text-neutral-600">
                        Optional: Click "Browse" to select from uploaded images or paste an external URL
                      </p>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={video.description || ''}
                      onChange={(e) => updateVideo(index, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Optional video description..."
                    />
                  </div>
                </div>

                {/* Video Preview */}
                {video.videoUrl && (
                  <div className="mt-4 p-4 bg-neutral-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <FiPlay size={16} className="text-primary" />
                      <span className="text-sm font-medium text-neutral-700">Preview</span>
                    </div>
                    <div className="text-sm text-neutral-600">
                      <div><strong>Title:</strong> {video.title}</div>
                      <div><strong>Artist:</strong> {video.artist}</div>
                      <div><strong>Runtime:</strong> {video.runtime}</div>
                      <div><strong>URL:</strong> {video.videoUrl}</div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}

            {formData.videos.length === 0 && (
              <div className="text-center py-12 text-neutral-500">
                <FiPlay size={48} className="mx-auto mb-4 opacity-50" />
                <p>No videos added yet. Click "Add Video" to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-neutral-200">
          <button
            type="submit"
            disabled={saving}
            onClick={(e) => {
              console.log('🖱️ Save button clicked!')
              console.log('Button type:', e.currentTarget.type)
              console.log('Form element:', e.currentTarget.form)
            }}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSave size={16} />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </form>

      {/* Video Picker Modal */}
      <VideoPicker
        isOpen={showVideoPicker}
        onClose={() => {
          setShowVideoPicker(false)
          setCurrentVideoIndex(null)
        }}
        onSelect={handleVideoSelect}
        selectedUrl={currentVideoIndex !== null ? formData.videos[currentVideoIndex]?.videoUrl : ''}
      />

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => {
          setShowMediaPicker(false)
          setCurrentVideoIndex(null)
        }}
        onSelect={handlePosterSelect}
        selectedUrl={currentVideoIndex !== null ? formData.videos[currentVideoIndex]?.poster : ''}
      />
    </div>
  )
}

export default VideoSectionEditor
